import {Field, useFormikContext} from 'formik';
import {Box, Typography} from '@mui/material';
import {BODY2, FIELD, FIELD_LABEL, OUTLINE, PLACEHOLDER, SUBTITLE1} from '@/utils/constants';
import Input from '@/shared/components/Inputs/Input';
import {EventSettingFormStyle} from '../EventSettingForm/EventSettingForm.style';
import {CreateEventFormValues} from '@/components/Organization/Dashboard/CreateNewEvent/formConfiguration';
import {getErrorMessage, setRequiredLabel} from '@/utils/helpers';
import DatePicker from '@/shared/components/DatePicker';
import Switch from '@/shared/components/Switches/Switch';
import TimePicker from '@/shared/components/TimePicker';
import GreyUploadImage from '@/shared/components/UploadDropzone/GreyUploadImage';
import Button from '@/shared/components/Button';
import {useEffect} from 'react';
import {initialRaffleTires} from '@/components/Organization/Dashboard/CreateNewEvent/TableAndTickets/TicketsTables/helpers';
import {OrganizationEventService} from '@/utils/services';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {toast} from 'react-toastify';
import {useAuctionTimer} from '@/utils/hooks';
import CurrencyField from '@/shared/components/Inputs/CurrencyInput';
import {getTeamSelector} from '@/store/organization';
import {ITeamRole} from '@/utils/enums';

const EventWebPageForm = () => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    values,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<CreateEventFormValues>();

  const currentEvent = useAppSelector(getCurrentEventSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;
  const {hasEnded} = useAuctionTimer(currentEvent?.raffleInfo?.endRaffleDate);

  const isDisebledPickWinnerButton =
    !values.isRaffle || values.raffleTires.length === 0 || !hasEnded || currentEvent?.hasActiveRaffleWinner;

  const removeTableClickHandler = (index: number) => {
    if (values.raffleTires.length === 1) return;

    const newRaffleTires = values.raffleTires.filter((_, i) => i !== index);
    setFieldValue('raffleTires', newRaffleTires);
  };

  useEffect(() => {
    if (!values.raffleTires.length) {
      addTableClickHandler();
    }
  }, [values.raffleTires.length]);

  const addTableClickHandler = () => {
    setFieldValue('raffleTires', [...values.raffleTires, initialRaffleTires]);
  };

  const handlePickWinnerClick = async () => {
    if (!currentEvent) return;
    try {
      await OrganizationEventService.getRaffleWinner(currentEvent.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (!values.isRaffle) {
      setFieldTouched('startRaffleDate', false);
      setFieldTouched('endRaffleDate', false);
      setFieldTouched('raffleDescription', false);
    }
  }, [values.isRaffle]);

  useEffect(() => {
    if (!values.isOnlineAuction) {
      setFieldTouched('startAuctionDate', false);
      setFieldTouched('endAuctionDate', false);
    }
  }, [values.isOnlineAuction]);

  return (
    <Box>
      <Box>
        <Box sx={EventSettingFormStyle.auctionWrapper}>
          <Box sx={EventSettingFormStyle.raffle}>
            <Typography
              variant={SUBTITLE1}
              sx={{...EventSettingFormStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}
            >
              Auction:
            </Typography>
            <Field
              id={FIELD.isOnlineAuction}
              name={FIELD.isOnlineAuction}
              component={Switch}
              checked={values.isOnlineAuction}
              disabled={isStandartMember}
            />
            <Typography
              variant={BODY2}
              sx={{...EventSettingFormStyle.subtitle, ...(isStandartMember && {color: 'text.disabled'})}}
            >
              Online auction
            </Typography>
          </Box>

          <Box sx={EventSettingFormStyle.dateWrapper}>
            <Box sx={EventSettingFormStyle.date}>
              <Field
                id={FIELD.startAuctionDate}
                name={FIELD.startAuctionDate}
                label={setRequiredLabel(FIELD_LABEL.startAuctionDate)}
                component={DatePicker}
                disabled={!values.isOnlineAuction || isStandartMember}
                sx={EventSettingFormStyle.dateMonthYear}
              />
              <Field
                id={FIELD.startAuctionTime}
                name={FIELD.startAuctionTime}
                label={setRequiredLabel(FIELD_LABEL.startTime)}
                component={TimePicker}
                disabled={!values.isOnlineAuction || isStandartMember}
                parentClassName={EventSettingFormStyle.time}
              />
            </Box>

            <Box sx={EventSettingFormStyle.date}>
              <Field
                id={FIELD.endAuctionDate}
                name={FIELD.endAuctionDate}
                label={setRequiredLabel(FIELD_LABEL.endAuctionDate)}
                component={DatePicker}
                disabled={!values.isOnlineAuction || isStandartMember}
                sx={EventSettingFormStyle.dateMonthYear}
              />
              <Field
                id={FIELD.endAuctionTime}
                name={FIELD.endAuctionTime}
                label={setRequiredLabel(FIELD_LABEL.endTime)}
                component={TimePicker}
                disabled={!values.isOnlineAuction || isStandartMember}
                parentClassName={EventSettingFormStyle.time}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={EventSettingFormStyle.auctionWrapper}>
          <Box sx={EventSettingFormStyle.raffle}>
            <Typography variant={SUBTITLE1} sx={{...EventSettingFormStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}>
              Fund the Mission:
            </Typography>
            <Field
              id={FIELD.isFundTheMission}
              name={FIELD.isFundTheMission}
              component={Switch}
              checked={values.isFundTheMission}
              disabled={isStandartMember}
            />
            <Typography variant={BODY2} sx={{...EventSettingFormStyle.subtitle, ...(isStandartMember && {color: 'text.disabled'})}}>
              Visible on event webpage
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={EventSettingFormStyle.wrapper}>
        <Box sx={EventSettingFormStyle.raffleWrap}>
          <Typography variant={SUBTITLE1} sx={{...EventSettingFormStyle.title, ...(isStandartMember && {color: 'text.disabled'})}}>
            Raffle:
          </Typography>

          <Field id={FIELD.isRaffle} name={FIELD.isRaffle} component={Switch} checked={values.isRaffle} disabled={isStandartMember} />

          <Typography variant={BODY2} sx={{...EventSettingFormStyle.subtitle, ...(isStandartMember && {color: 'text.disabled'})}}>
            Visible on event webpage
          </Typography>
        </Box>

        <Box sx={EventSettingFormStyle.dateWrapper}>
          <Box sx={EventSettingFormStyle.date}>
            <Field
              id={FIELD.startRaffleDate}
              name={FIELD.startRaffleDate}
              label={setRequiredLabel(FIELD_LABEL.startRaffleDate)}
              component={DatePicker}
              sx={EventSettingFormStyle.dateMonthYear}
              disabled={!values.isRaffle || isStandartMember}
            />
            <Field
              id={FIELD.startRaffleTime}
              name={FIELD.startRaffleTime}
              label={setRequiredLabel(FIELD_LABEL.startTime)}
              component={TimePicker}
              parentClassName={EventSettingFormStyle.time}
              disabled={!values.isRaffle || isStandartMember}
            />
          </Box>

          <Box sx={EventSettingFormStyle.date}>
            <Field
              id={FIELD.endRaffleDate}
              name={FIELD.endRaffleDate}
              label={setRequiredLabel(FIELD_LABEL.endRaffleDate)}
              component={DatePicker}
              sx={EventSettingFormStyle.dateMonthYear}
              disabled={!values.isRaffle || isStandartMember}
            />
            <Field
              id={FIELD.endRaffleTime}
              name={FIELD.endRaffleTime}
              label={setRequiredLabel(FIELD_LABEL.endTime)}
              component={TimePicker}
              parentClassName={EventSettingFormStyle.time}
              disabled={!values.isRaffle || isStandartMember}
            />
          </Box>
        </Box>
        <Box>
          <Field
            id={FIELD.raffleDescription}
            name={FIELD.raffleDescription}
            rows={3}
            sx={EventSettingFormStyle.raffleDescription}
            placeholder={PLACEHOLDER.raffleDescription}
            label={FIELD_LABEL.raffleDescription}
            component={Input}
            multiline
            disabled={!values.isRaffle || isStandartMember}
          />

          <Box sx={EventSettingFormStyle.prize}>
            <Box sx={EventSettingFormStyle.prizeLogoWrapper}>
              <Field
                id={FIELD.prizeLogo}
                name={FIELD.prizeLogo}
                label={FIELD_LABEL.prizeLogo}
                component={GreyUploadImage}
                parentClassName={{
                  ...EventSettingFormStyle.prizeLogo,
                  ...((!values.isRaffle || isStandartMember) && {pointerEvents: 'none', opacity: 0.5}),
                }}
                disabled={!values.isRaffle || isStandartMember}
              />
            </Box>

            <Box sx={EventSettingFormStyle.raffleDescriptionWrapper}>
              {values.raffleTires.map((raffle, index) => (
                <Box key={raffle.id} sx={{display: 'flex', gap: '12px', width: '100%'}}>
                  <Field
                    id={`raffleTires[${index}].numberOfTickets`}
                    name={`raffleTires[${index}].numberOfTickets`}
                    sx={EventSettingFormStyle.tickets}
                    parentClassName={{width: '100%'}}
                    label={FIELD_LABEL.tickets}
                    component={Input}
                    isArrayData
                    type="text"
                    inputProps={{
                      inputMode: 'numeric',
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                      },
                    }}
                    disabled={!values.isRaffle || isStandartMember}
                  />

                  <CurrencyField
                    name={`raffleTires[${index}].price`}
                    label={FIELD_LABEL.ticketPrice}
                    placeholder={`$ ${PLACEHOLDER.zero}`}
                    isArrayData
                    disabled={!values.isRaffle || isStandartMember}
                  />
                </Box>
              ))}

              <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%'}}>
                <Button
                  title="Add new level"
                  variant="text"
                  size="small"
                  onClick={() => addTableClickHandler()}
                  sx={{
                    marginLeft: 'auto',
                  }}
                  disabled={!values.isRaffle || values.raffleTires.length === 3 || isStandartMember}
                />

                <Button
                  title="Remove last level"
                  variant="text"
                  size="small"
                  onClick={() => removeTableClickHandler(values.raffleTires.length - 1)}
                  sx={{
                    marginLeft: 'auto',
                  }}
                  disabled={!values.isRaffle || values.raffleTires.length === 1 || isStandartMember}
                />
              </Box>

              <Field
                id={FIELD.quantity}
                name={FIELD.quantity}
                placeholder={PLACEHOLDER.unlimited}
                label={FIELD_LABEL.ticketsQuantityRaffle}
                component={Input}
                disabled={!values.isRaffle || isStandartMember}
              />

              <Button
                title="Pick raffle winner"
                variant={OUTLINE}
                color="primary"
                disabled={isDisebledPickWinnerButton || isStandartMember}
                onClick={handlePickWinnerClick}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EventWebPageForm;
